import { apiFetch } from './client'

/** React Query key for the admin's hospital + beds, scoped by the selected
 * hospital id (null for a hospital_admin, who has exactly one). Shared so the
 * bed editor (M2-T08) invalidates exactly what the home page (M2-T06) reads. */
export const HOSPITAL_ME_KEY = ['admin', 'hospitals', 'me'] as const
export function hospitalMeKey(hospitalId: string | null) {
  return [...HOSPITAL_ME_KEY, hospitalId] as const
}

export type HospitalSummary = {
  id: string
  slug: string
  name: string
  city: string
  address: string
  phone: string
}

export type BedState = {
  icu_total: number
  icu_free: number
  general_total: number
  general_free: number
  private_total: number
  private_free: number
  executive_total: number
  executive_free: number
  maternity_total: number
  maternity_free: number
  emergency_status: 'open' | 'divert' | 'closed'
  ambulance_available: boolean
  updated_at: string
}

export type HospitalMe = {
  hospital: HospitalSummary
  beds: BedState
  multiple_hospitals: boolean
}

/** GET /api/v1/admin/hospitals/me — the admin's hospital + bed inventory.
 * A hospital_admin resolves to their own hospital; an amtc_admin oversees all
 * 10, so they must name one via `hospitalId` (?hospital_id=). */
export async function fetchMyHospital(
  token: string | null,
  hospitalId?: string | null,
): Promise<HospitalMe> {
  const qs = hospitalId ? `?hospital_id=${encodeURIComponent(hospitalId)}` : ''
  const body = await apiFetch<{ data: HospitalMe }>(
    `/api/v1/admin/hospitals/me${qs}`,
    { token },
  )
  return body.data
}

/** A hospital the amtc_admin can pick to manage (from the public list). */
export type HospitalOption = { id: string; name: string; city: string }

export const HOSPITAL_OPTIONS_KEY = ['admin', 'hospital-options'] as const

/** GET /api/v1/hospitals — the closed list of 10, for the amtc hospital picker.
 * Public endpoint (the token is ignored); we only need id/name/city. */
export async function fetchHospitalOptions(
  token: string | null,
): Promise<HospitalOption[]> {
  const body = await apiFetch<{
    data: Array<{ id: string; name: string; city: string }>
  }>('/api/v1/hospitals', { token })
  return body.data
    .map((h) => ({ id: h.id, name: h.name, city: h.city }))
    .sort((a, b) => a.name.localeCompare(b.name))
}
